import { TCardFacet, TFacet } from '@/types/collection'
import { urlBuilder } from './url-builder.service'

export interface FilterTag {
  key: string
  title: string
  parentKey: string
  value: string
}

/**
 * Get filter tags directly from URL instead of store
 * This ensures consistency with the actual URL state
 */
export const getFilterTagsFromUrl = (
  url: URL,
  originalSlug: string,
  facets: TFacet[] | null,
  cardFacets: TCardFacet[] | null
): FilterTag[] => {
  const tags: FilterTag[] = []
  if (!facets && !cardFacets) return tags

  // Use unified parser from UrlBuilderService
  const { filters: urlFilters } = urlBuilder.parseUrl(
    url,
    originalSlug,
    facets || [],
    cardFacets || []
  )

  const allFacetValues = new Map<string, string>()

  // Map normal facets
  facets?.forEach((facet) => {
    facet.facet_values.forEach((value) => {
      allFacetValues.set(`${facet.seo_alias}-${value.seo_alias}`, value.title)
    })
  })

  // Map card facets
  cardFacets?.forEach((facet) => {
    facet.facet_values.forEach((value) => {
      allFacetValues.set(`${facet.seo_alias}-${value.seo_alias}`, value.title)
    })
  })

  // Add special handling for gender_type filter
  const GENDER_TYPE_MAP: Record<string, string> = {
    male: 'Male',
    female: 'Female',
    MALE: 'Male',
    FEMALE: 'Female',
  }

  // Generate tags from parsed URL filters
  for (const key in urlFilters) {
    const value = urlFilters[key]
    if (
      value &&
      key !== 'sort' &&
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'slug' &&
      key !== 'slug_concat' // Note: slug_concat is handled separately below
    ) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          // Special handling for gender_type
          if (key === 'gender_type' && GENDER_TYPE_MAP[v]) {
            tags.push({
              key: `${key}-${v}`,
              title: GENDER_TYPE_MAP[v],
              parentKey: key,
              value: v,
            })
          } else {
            const lookupKey = `${key}-${v}`
            const title = allFacetValues.get(lookupKey)
            if (title) {
              tags.push({
                key: `${key}-${v}`,
                title,
                parentKey: key,
                value: v,
              })
            }
          }
        })
      } else if (typeof value === 'string') {
        // Special handling for gender_type
        if (key === 'gender_type' && GENDER_TYPE_MAP[value]) {
          tags.push({
            key: `${key}-${value}`,
            title: GENDER_TYPE_MAP[value],
            parentKey: key,
            value: value,
          })
        } else {
          const lookupKey = `${key}-${value}`
          const title = allFacetValues.get(lookupKey)
          if (title) {
            tags.push({
              key: `${key}-${value}`,
              title,
              parentKey: key,
              value: value,
            })
          }
        }
      }
    }
  }

  // Handle slug_concat as fallback for unmapped concat values
  if (urlFilters.slug_concat && typeof urlFilters.slug_concat === 'string') {
    // Try to find a matching facet value for the slug_concat
    const allFacets = [...(facets || []), ...(cardFacets || [])]
    let foundMatch = false

    for (const facet of allFacets) {
      if (!facet?.is_concat_slug) continue

      const matchingValue = facet.facet_values?.find(
        (value) => value.seo_alias === urlFilters.slug_concat
      )

      if (matchingValue) {
        tags.push({
          key: `${facet.seo_alias}-${matchingValue.seo_alias}`,
          title: matchingValue.title,
          parentKey: facet.seo_alias,
          value: matchingValue.seo_alias,
        })
        foundMatch = true
        break
      }
    }
  }

  return tags
}
